import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { createClient } from '@supabase/supabase-js';
import {
  AlertTriangle,
  Boxes,
  ChevronRight,
  CircleDollarSign,
  FileSpreadsheet,
  CloudOff,
  History,
  LayoutDashboard,
  Loader2,
  LockKeyhole,
  LogOut,
  Menu,
  PackagePlus,
  ReceiptText,
  Settings2,
  ShieldCheck,
  ShoppingCart,
  UsersRound,
  Wifi,
  X,
} from 'lucide-react';
import {
  checkoutPayload,
  isRetryableCheckoutError,
  productPhotoPath,
  normalizeGalleryImages,
  totals,
  validateProductPhoto,
} from './domain.js';
import {
  enqueueCheckout,
  flushQueuedCheckouts,
  holdCart,
  listHeldCarts,
  listQueuedCheckouts,
  removeHeldCart,
} from './offline.js';
import {
  Audit,
  Cash,
  Customers,
  Dashboard,
  Pos,
  Products,
  ReceiptModal,
  Settings,
  Stock,
} from './screens.jsx';
import { Brand, Button, Notice } from './ui.jsx';
import CatalogImport from './CatalogImport.jsx';
import StaffManagement from './StaffManagement.jsx';
import PresalesReport from './PresalesReport.jsx';
import DeviceActivationPilot from './DeviceActivationPilot.jsx';
import TerminalOperatorAdminPilot from './TerminalOperatorAdminPilot.jsx';
import NfeReceiving from './NfeReceiving.jsx';
import {prepareSharedPresale} from './pilotSharedCart.js';
import {resolveSmartPdvTenant} from './tenantRoute.js';
import './styles.css';
import './mobile-ux.css';
import './staff.css';
import {uploadWithRecovery,readableUploadError} from './photoUpload.js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const recoveryRedirect = `${location.origin}/?mode=recovery`;
const initialRecovery =
  new URLSearchParams(location.search).get('mode') === 'recovery' ||
  location.hash.includes('type=recovery');
const supabase =
  SUPABASE_URL && SUPABASE_KEY
    ? createClient(SUPABASE_URL, SUPABASE_KEY, {
        auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
      })
    : null;
const isDemo = import.meta.env.DEV && new URLSearchParams(location.search).get('demo') === '1';
const requestedOrganizationSlug = new URLSearchParams(location.search).get('org') || '';
const uuid = () => crypto.randomUUID();
const now = () => new Date().toISOString();
const PRODUCT_IMAGE_BUCKET = 'repair-product-images';
const NAV_STATE_KEY = 'kaizen.smartpdv.navigation';
const RELEASE_SHA = String(import.meta.env.VITE_SMARTPDV_RELEASE_SHA || '').slice(0,12);

function applySmartPdvTheme(theme = {}) {
  const mode = ['light','dark','system'].includes(theme?.mode) ? theme.mode : 'light';
  document.documentElement.style.setProperty('--brand', theme?.primary_color || '#635bff');
  document.documentElement.style.setProperty('--accent', theme?.accent_color || '#0891b2');
  document.documentElement.dataset.theme = mode;
  const resolvedDark = mode === 'dark' || (mode === 'system' && globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches);
  document.querySelector('meta[name="theme-color"]')?.setAttribute(
    'content',
    resolvedDark ? '#070b14' : '#f6f7fb',
  );
}

const DEMO_ORG = { id: 'demo-org', slug: 'casa-da-limpeza', name: 'Casa da Limpeza' };
const DEMO = {
  settings: {
    organization_id: 'demo-org',
    display_name: 'Casa da Limpeza',
    plan_tier: 'advanced',
    theme: { mode: 'light', primary_color: '#635bff', accent_color: '#0891b2' },
    operations: {
      max_cashier_discount_percent: 10,
      allow_negative_stock: false,
      require_customer: false,
      low_stock_notifications: true,
    },
    receipt: { footer_message: 'Obrigado pela preferência. Volte sempre!' },
    integrations: {
      kpay: { enabled: false, status: 'adapter_ready' },
      repair: { enabled: true, status: 'active' },
      fiscal: { enabled: false, status: 'not_configured' },
      evo: { enabled: false, status: 'contract_ready' },
    },
  },
  locations: [{ id: 'loc1', name: 'Loja Matriz', code: 'MATRIZ', currency: 'BRL' }],
  terminals: [{ id: 'term1', location_id: 'loc1', name: 'Caixa 01', code: 'CX-01' }],
  categories: [
    { id: 'cat1', name: 'Destaques', color: '#6d63ff', active: true },
    { id: 'cat2', name: 'Limpeza', color: '#29d7ee', active: true },
    { id: 'cat3', name: 'Descartáveis', color: '#ffb15c', active: true },
  ],
  products: [
    { id: 'p1', category_id: 'cat1', sku: 'KDL-001', barcode: '7891000000014', name: 'Kit Limpeza Pesada', description: 'Kit completo profissional', brand: 'Kaizen Clean', supplier: 'CDL', unit: 'UN', price_cents: 18990, cost_cents: 10200, min_stock: 5, track_inventory: true, allow_fractional: false, active: true, tax_origin: 0 },
    { id: 'p2', category_id: 'cat2', sku: 'KDL-014', barcode: '7891000000144', name: 'Desengordurante 5L', description: 'Concentrado profissional', brand: 'Kaizen Clean', supplier: 'CDL', unit: 'UN', price_cents: 6490, cost_cents: 3420, min_stock: 8, track_inventory: true, allow_fractional: false, active: true, tax_origin: 0 },
    { id: 'p3', category_id: 'cat2', sku: 'KDL-022', barcode: '7891000000229', name: 'Limpador Perfumado 2L', description: 'Fragrância fresh', brand: 'Kaizen Clean', supplier: 'CDL', unit: 'UN', price_cents: 2790, cost_cents: 1480, min_stock: 6, track_inventory: true, allow_fractional: false, active: true, tax_origin: 0 },
    { id: 'p4', category_id: 'cat3', sku: 'KDL-040', barcode: '7891000000403', name: 'Pano Microfibra', description: 'Kit com 5 unidades', brand: 'HupMix', supplier: 'CDL', unit: 'PCT', price_cents: 1990, cost_cents: 920, min_stock: 10, track_inventory: true, allow_fractional: false, active: true, tax_origin: 0 },
  ],
  customers: [
    { id: 'c1', name: 'Marina Oliveira', phone: '(48) 99912-2210', email: 'marina@example.com', status: 'active', marketing_consent: true },
    { id: 'c2', name: 'Restaurante Atlântico', phone: '(48) 3222-8100', email: 'compras@example.com', status: 'active', marketing_consent: false },
  ],
  balances: [
    { product_id: 'p1', location_id: 'loc1', quantity: 18 },
    { product_id: 'p2', location_id: 'loc1', quantity: 7 },
    { product_id: 'p3', location_id: 'loc1', quantity: 22 },
    { product_id: 'p4', location_id: 'loc1', quantity: 32 },
  ],
  sessions: [{ id: 'session1', location_id: 'loc1', terminal_id: 'term1', status: 'open', opening_cents: 20000, opened_at: now() }],
  sales: [
    { id: 'sale1018', sale_number: 1018, status: 'completed', total_cents: 18990, receipt_type: 'non_fiscal', created_at: now(), customer_id: 'c1', payment_summary: [{ method: 'pix', amount_cents: 18990 }] },
    { id: 'sale1017', sale_number: 1017, status: 'completed', total_cents: 8480, receipt_type: 'non_fiscal', created_at: new Date(Date.now() - 900000).toISOString(), payment_summary: [{ method: 'cash', amount_cents: 8480 }] },
  ],
  movements: [{ id: 'm1', session_id: 'session1', movement_type: 'opening', amount_cents: 20000, reason: 'Abertura do caixa', created_at: now() }],
  inventoryMovements: [],
  auditEvents: [],
  taxRules: [],
  fiscalDocuments: [],
  capabilities: {
    role: 'owner', sell: true, register: true, catalog: true, inventory: true, cash_management: true,
    void_sales: true, discount_override: true, settings: true, audit: true,
  },
};

function cloneDemo() {
  return structuredClone(DEMO);
}

function value(result) {
  if (result.error) throw new Error(result.error.message);
  return result.data;
}

function friendlyError(error) {
  const message = String(error?.message || error || 'Falha inesperada');
  const known = {
    pdv_access_denied: 'Acesso ao SmartPDV não autorizado para esta empresa.',
    pdv_admin_required: 'Somente administradores podem alterar estas configurações.',
    catalog_permission_required: 'Seu perfil não pode alterar o catálogo.',
    cash_permission_required: 'Seu perfil não pode movimentar o caixa.',
    register_not_open: 'O caixa deste terminal não está aberto.',
    insufficient_cash: 'O valor da sangria supera o dinheiro esperado.',
    insufficient_stock: 'Estoque insuficiente para concluir a operação.',
    discount_limit_exceeded: 'O desconto supera o limite do seu perfil.',
    price_changed_refresh_cart: 'Um preço mudou. Atualize o carrinho e tente novamente.',
    product_sku_or_barcode_already_exists: 'SKU ou código de barras já cadastrado.',
    invalid_ncm: 'O NCM deve conter 8 dígitos.',
    invalid_cest: 'O CEST deve conter 7 dígitos.',
  };
  const key = Object.keys(known).find((item) => message.includes(item));
  return key ? known[key] : message;
}

async function attachProductImages(products = []) {
  const paths = [...new Set(products.flatMap((product) => [
    product.image_path,
    ...(product.metadata?.gallery_images || []).map(item => item.path),
  ]).filter(Boolean))];
  if (!paths.length) return products;
  const result = await supabase.storage
    .from(PRODUCT_IMAGE_BUCKET)
    .createSignedUrls(paths, 60 * 60);
  if (result.error) return products;
  const urls = new Map(
    (result.data || [])
      .filter((item) => item?.path && item?.signedUrl)
      .map((item) => [item.path, item.signedUrl]),
  );
  return products.map((product) => ({
    ...product,
    image_url: product.image_path ? urls.get(product.image_path) || null : null,
    metadata: {
      ...(product.metadata || {}),
      gallery_images: normalizeGalleryImages(product.metadata?.gallery_images, product.image_path, product.organization_id)
        .map(item => ({...item, image_url: urls.get(item.path) || null})),
    },
  }));
}

async function uploadProductPhoto(organizationId, file) {
  const validation = validateProductPhoto(file);
  if (!validation.valid) throw new Error(validation.error);
  const path = productPhotoPath(organizationId, uuid(), validation.type);
  if (!path) throw new Error('Não foi possível preparar o caminho seguro da fotografia.');
  const bucket = supabase.storage.from(PRODUCT_IMAGE_BUCKET);
  try {
    await uploadWithRecovery(bucket, path, file, {
      cacheControl:'3600',contentType:validation.type
    });
  } catch(error) {
    throw new Error(readableUploadError(error),{cause:error});
  }
  // A temporary signed URL is a display convenience, not a dependency of
  // persisting the private image path in the catalog. Reload signs it again.
  const signed = await bucket.createSignedUrl(path,60*60).catch(()=>({data:null}));
  return {
    image_path:path,
    image_mime_type:validation.type,
    image_url:signed?.data?.signedUrl||null
  };
}

async function loadAccess(userId) {
  const memberships = value(await supabase.from('organization_members').select('organization_id,user_id,role,display_name,permissions,status').eq('user_id', userId).eq('status', 'active')) || [];
  if (!memberships.length) return { organizations: [], memberships, entitlements: [] };
  const ids = memberships.map((membership) => membership.organization_id);
  const [organizationsResult, productResult] = await Promise.all([
    supabase.from('organizations').select('id,name,slug,status').in('id', ids).in('status', ['active', 'onboarding']).order('name'),
    supabase.from('kaizen_products').select('id,slug,status').eq('slug', 'smart-pdv').neq('status', 'disabled').maybeSingle(),
  ]);
  const organizations = value(organizationsResult) || [];
  const product = value(productResult);
  if (!product) return { organizations: [], memberships, entitlements: [] };
  const [entitlementsResult, profileResult] = await Promise.all([
    supabase.from('organization_product_entitlements').select('organization_id,product_id,status,settings').eq('product_id', product.id).in('organization_id', ids),
    supabase.from('kaizen_operator_profiles').select('preferences').eq('user_id', userId).maybeSingle(),
  ]);
  const entitlements = value(entitlementsResult) || [];
  // A preference cannot grant tenant access; the resolver checks the licensed org list.
  const preferredOrganizationSlug = profileResult.error ? '' :
    String(profileResult.data?.preferences?.smartpdv_default_organization_slug || '');
  const active = new Set(
    entitlements
      .filter((entitlement) => {
        if (!['active','trial'].includes(entitlement.status)) return false;
        if (entitlement.settings?.trial_ends_at && Date.parse(entitlement.settings.trial_ends_at) <= Date.now()) return false;
        const member=memberships.find((m)=>m.organization_id===entitlement.organization_id);
        return !entitlement.settings?.internal_only || member?.permissions?.internal_operator===true;
      })
      .map((entitlement) => entitlement.organization_id),
  );
  return {
    organizations: organizations.filter((organization) => active.has(organization.id)),
    memberships: memberships.filter((membership) => active.has(membership.organization_id)),
    entitlements,
    preferredOrganizationSlug,
  };
}

async function loadTenantPages(table, organizationId) {
  const items=[];
  for(let from=0;from<30000;from+=500){
    const next=value(await supabase.from(table).select('*')
      .eq('organization_id',organizationId).order('id').range(from,from+499))||[];
    items.push(...next);
    if(next.length<500)return items;
  }
  throw new Error('Catálogo acima do limite de consulta; suporte necessário.');
}

async function loadAllBalances(organizationId){
  const collected=[];
  for(let from=0;from<30000;from+=500){
    const page=value(await supabase.from('pdv_stock_balances').select('*')
      .eq('organization_id',organizationId).order('location_id').order('product_id')
      .range(from,from+499))||[];
    collected.push(...page);
    if(page.length<500)return collected;
  }
  throw new Error('Saldos acima do limite de consulta.');
}
// A stalled identity, DB or network request must never create an endless spinner.
function withDeadline(promise,limitMs=15000){
  let timer;
  return Promise.race([Promise.resolve(promise),new Promise((_,reject)=>{
    timer=setTimeout(()=>reject(new Error('Tempo de conexão excedido. Tente novamente.')),limitMs);
  })]).finally(()=>clearTimeout(timer));
}

async function loadData(organizationId,{quick=false}={}) {
  const organizationState=value(await supabase.from('organizations').select('status').eq('id',organizationId).single());
  if(organizationState?.status==='active'){
    value(await supabase.rpc('pdv_initialize_tenant', { p_organization_id: organizationId }));
  }else if(organizationState?.status!=='onboarding'){
    throw new Error('Empresa não autorizada para o SmartPDV.');
  }
  // During internal onboarding the administrator provisions location/settings explicitly;
  // do not attempt the active-only initializer or silently activate the whole organization.
  const entries = [
    ['settings', supabase.from('pdv_settings').select('*').eq('organization_id', organizationId).single()],
    ['locations', supabase.from('pdv_locations').select('*').eq('organization_id', organizationId).eq('status', 'active').order('name')],
    ['terminals', supabase.from('pdv_terminals').select('*').eq('organization_id', organizationId).eq('status', 'active').order('name')],
    ['categories', supabase.from('pdv_categories').select('*').eq('organization_id', organizationId).order('sort_order').order('name')],
    ['customers', supabase.from('pdv_customers').select('*').eq('organization_id', organizationId).eq('status', 'active').order('name').limit(500)],
    ['sessions', supabase.from('pdv_register_sessions').select('*').eq('organization_id', organizationId).order('opened_at', { ascending: false }).limit(30)],
    ['sales', supabase.from('pdv_sales').select('id,sale_number,status,subtotal_cents,discount_cents,total_cents,tendered_cents,change_cents,receipt_type,customer_id,register_session_id,payment_summary,created_at').eq('organization_id', organizationId).order('created_at', { ascending: false }).limit(100)],
    ['movements', supabase.from('pdv_cash_movements').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }).limit(200)],
    ['inventoryMovements', supabase.from('pdv_inventory_movements').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }).limit(200)],
    ['auditEvents', supabase.from('pdv_audit_events').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }).limit(200)],
    ['taxRules', supabase.from('pdv_tax_rules').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }).limit(100)],
    ['fiscalDocuments', supabase.from('pdv_fiscal_documents').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }).limit(100)],
    ['capabilities', supabase.rpc('pdv_member_capabilities', { p_organization_id: organizationId })],
  ];
  const results = await Promise.all(entries.map(([, query]) => query));
  const loaded = Object.fromEntries(results.map((result, index) => {
    const name = entries[index][0];
    return [name, value(result) ?? (name === 'settings' ? null : name === 'capabilities' ? {} : [])];
  }));
  // Do not hold the mobile shell hostage until all 4,709+ products and
  // thousands of stock balances are transferred. Hydrate the rest separately.
  const [products,balances]=await Promise.all([
    quick ? value(await supabase.from('pdv_products').select('*')
       .eq('organization_id',organizationId).order('name').order('id').limit(80))||[]
      : loadTenantPages('pdv_products',organizationId),
    quick ? value(await supabase.from('pdv_stock_balances').select('*')
       .eq('organization_id',organizationId).order('location_id').order('product_id').limit(120))||[]
      : loadAllBalances(organizationId),
  ]);
  loaded.products = await attachProductImages(products.sort((a,b)=>a.name.localeCompare(b.name,'pt-BR')));
  loaded.balances = balances;
  // Advisory-only channel status; a missing RLS grant must not block the cashier.
  const connections=await supabase.from('channel_connections')
    .select('provider,status').eq('organization_id',organizationId);
  loaded.channelConnections=connections.error?[]:(connections.data||[]);
  return loaded;
}

function AuthShell({ children }) {
  return (
    <div className="auth">
      <section className="authPitch">
        <Brand />
        <div>
          <p className="eyebrow">OPERAÇÃO COM SOBERANIA</p>
          <h1>Venda simples na frente.<br />Governança sólida por trás.</h1>
          <p>Caixa, produtos, estoque e clientes por tenant, com integrações desacopladas e trilha auditável.</p>
        </div>
        <div className="trust">
          <span><ShieldCheck size={17} />Tenant isolado</span>
          <span><LockKeyhole size={17} />Identidade protegida</span>
          <span><ReceiptText size={17} />Documento não fiscal</span>
        </div>
      </section>
      <section className="loginCard">{children}</section>
    </div>
  );
}

function Login() {
  const invitationToken = new URLSearchParams(location.search).get('invite');
  const [mode, setMode] = useState(invitationToken?'register':'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  function switchMode(next) {
    setMode(next);
    setError('');
    setMessage('');
    setPassword('');
  }

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    const result = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    if (result.error) setError('E-mail ou senha inválidos.');
  }

  async function register(event) {
    event.preventDefault();setBusy(true);setError('');setMessage('');
    if(!invitationToken || password.length<12){
      setBusy(false);setError('Use uma senha de pelo menos 12 caracteres.');return;
    }
    const {data,error:e}=await supabase.auth.signUp({
      email,password,options:{emailRedirectTo:location.origin+'/?invite='+encodeURIComponent(invitationToken)}
    });
    setBusy(false);
    if(e){setError('Não foi possível criar seu acesso. Confira o e-mail ou entre com uma conta existente.');return;}
    if(!data?.session)setMessage('Confirme seu e-mail. Depois entre pelo mesmo link de convite.');
  }

  async function recover(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    const result = await supabase.auth.resetPasswordForEmail(email, { redirectTo: recoveryRedirect });
    setBusy(false);
    if (result.error) return setError(friendlyError(result.error));
    setMessage('Se este e-mail estiver cadastrado, enviaremos um link seguro para definir uma nova senha.');
  }

  return (
    <AuthShell>
      <p className="eyebrow">ACESSO KAIZEN</p>
      <h2>{mode === 'login' ? 'Entrar no SmartPDV' : mode==='register'?'Criar acesso de operador':'Recuperar acesso'}</h2>
      <p className="muted">{mode === 'login' ? 'Use a mesma identidade dos demais produtos Kaizen.' : mode==='register'?'Use seu e-mail convidado e confirme sua identidade.':'Informe o e-mail da sua conta.'}</p>
      <form onSubmit={mode === 'login' ? submit : mode==='register'?register:recover}>
        <label>E-mail<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
        {(mode==='login'||mode==='register') && <label>Senha<input type="password" minLength={mode==='register'?12:undefined} autoComplete={mode==='register'?'new-password':'current-password'} value={password} onChange={(event) => setPassword(event.target.value)} required /></label>}
        <Button disabled={busy}>
          {busy ? <Loader2 className="spin" size={17} /> : mode==='login'||mode==='register' ? <ChevronRight size={17} /> : <LockKeyhole size={17} />}
          {mode === 'login' ? 'Entrar' : mode==='register'?'Criar meu acesso':'Enviar link seguro'}
        </Button>
      </form>
      <button type="button" className="authLink" onClick={() => switchMode(mode === 'login' ? 'recovery' : 'login')}>
        {mode === 'login' ? 'Esqueci minha senha' : mode==='register'?'Já tenho uma conta — entrar':'Voltar para o acesso'}
      </button>
      {invitationToken&&mode!=='register'&&<button type="button" className="authLink" onClick={()=>switchMode('register')}>Criar nova conta de operador</button>}
      {message && <p className="success" aria-live="polite">{message}</p>}
      {error && <p className="error" aria-live="assertive">{error}</p>}
    </AuthShell>
  );
}

function PasswordRecovery({ onComplete }) {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(event) {
    event.preventDefault();
    setError('');
    if (password.length < 12) return setError('Use pelo menos 12 caracteres.');
    if (password !== confirmation) return setError('As senhas não coincidem.');
    setBusy(true);
    const result = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (result.error) return setError(friendlyError(result.error));
    history.replaceState({}, '', location.pathname);
    onComplete();
  }
  return (
    <AuthShell>
      <p className="eyebrow">SEGURANÇA KAIZEN</p>
      <h2>Definir nova senha</h2>
      <p className="muted">Crie uma senha exclusiva com pelo menos 12 caracteres.</p>
      <form onSubmit={submit}>
        <label>Nova senha<input type="password" autoComplete="new-password" minLength="12" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
        <label>Confirmar nova senha<input type="password" autoComplete="new-password" minLength="12" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required /></label>
        <Button disabled={busy}>{busy ? <Loader2 className="spin" size={17} /> : <ShieldCheck size={17} />}Salvar nova senha</Button>
      </form>
      {error && <p className="error" aria-live="assertive">{error}</p>}
    </AuthShell>
  );
}

const NAVIGATION = [
  { id: 'pos', label: 'Frente de caixa', short: 'PDV', icon: ShoppingCart },
  { id: 'dashboard', label: 'Visão geral', short: 'Visão', icon: LayoutDashboard },
  { id: 'products', label: 'Produtos', short: 'Produtos', icon: Boxes },
  { id: 'import', label: 'Importar produtos', short: 'Importar', icon: FileSpreadsheet, capability: 'catalog' },
  { id: 'stock', label: 'Estoque', short: 'Estoque', icon: PackagePlus },
  { id: 'customers', label: 'Clientes', short: 'Clientes', icon: UsersRound },
  { id: 'cash', label: 'Caixa', short: 'Caixa', icon: CircleDollarSign },
  { id: 'audit', label: 'Auditoria', short: 'Auditoria', icon: History, capability: 'audit' },
  { id: 'settings', label: 'Configurações', short: 'Ajustes', icon: Settings2 },
  { id: 'staff', label: 'Operadores', short: 'Equipe', icon: UsersRound, capability: 'settings' },
];

function Layout({ screen, setScreen, org, orgs, setOrg, user, online, register, offlineCount, capabilities, enabledScreens=null, screenLabels=null, children }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [mobileNavOpen,setMobileNavOpen]=useState(false);
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try {
      return localStorage.getItem(NAV_STATE_KEY) !== 'expanded';
    } catch {
      return true;
    }
  });
  const visibleNavigation = NAVIGATION.filter(item=>(!item.capability||capabilities?.[item.capability]) && (!enabledScreens||enabledScreens.includes(item.id))).map(item=>({...item,label:screenLabels?.[item.id]||item.label}));
  const primaryMobile = visibleNavigation.filter((item) => ['pos', 'dashboard', 'products', 'stock'].includes(item.id));
  const moreMobile = visibleNavigation.filter((item) => !primaryMobile.some((primary) => primary.id === item.id));
  const go = (id) => {
    setScreen(id);
    setMoreOpen(false);
    setMobileNavOpen(false);
  };
  useEffect(() => {
    try {
      localStorage.setItem(NAV_STATE_KEY, navCollapsed ? 'collapsed' : 'expanded');
    } catch {
      // The navigation remains usable even when storage is blocked by the browser.
    }
  }, [navCollapsed]);
  useEffect(()=>{
    if(!mobileNavOpen)return undefined;
    const onKeyDown=(event)=>{if(event.key==='Escape')setMobileNavOpen(false)};
    window.addEventListener('keydown',onKeyDown);
    return ()=>window.removeEventListener('keydown',onKeyDown);
  },[mobileNavOpen]);
  return (
    <div className={`appShell ${navCollapsed ? 'navCollapsed' : ''} ${mobileNavOpen ? 'mobileNavExpanded' : ''}`}>
      {mobileNavOpen&&<button type="button" className="mobileNavScrim"
        aria-label="Recolher navegação lateral" onClick={()=>setMobileNavOpen(false)}/>}
      <aside className="sideNav">
        <button
          type="button"
          className="brandToggle"
          aria-label={mobileNavOpen ? 'Recolher menu lateral no celular' : navCollapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
          aria-expanded={mobileNavOpen || !navCollapsed}
          title="Expandir ou recolher menu vertical"
          onClick={() => {
            if(window.matchMedia('(max-width:1040px)').matches){
              setMobileNavOpen(current=>!current);
            }else setNavCollapsed(current=>!current);
          }}
        >
          <Brand compact={navCollapsed && !mobileNavOpen} />
        </button>
        <div className="tenantSwitcher">
          <span>{org.name[0]}</span>
          <div><small>EMPRESA ATIVA</small><strong>{org.name}</strong></div>
          {orgs.length > 1 && <select aria-label="Trocar empresa" value={org.id} onChange={(event) => setOrg(event.target.value)}>{orgs.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>}
        </div>
        <nav aria-label="Menu vertical do SmartPDV">
          {visibleNavigation.map((item) => {
            const Icon = item.icon;
            return <button key={item.id} title={item.label} aria-label={item.label} aria-current={screen===item.id?'page':undefined} className={screen === item.id ? 'active' : ''} onClick={() => go(item.id)}><Icon size={19} /><span>{item.label}</span>{item.id === 'pos' && register && <i>ABERTO</i>}{item.id === 'dashboard' && offlineCount > 0 && <i className="warningCount">{offlineCount}</i>}</button>;
          })}
        </nav>
        <div className="sideBottom">
          <div className="version"><ReceiptText size={16} /><span><strong>Recibo não fiscal</strong><small>Fiscal protegido por gate</small></span></div>
          <button className="profile" onClick={() => (isDemo ? location.assign('?demo=1') : supabase.auth.signOut())}>
            <span>{(user || 'O')[0].toUpperCase()}</span><div><strong>{user}</strong><small>Sair com segurança</small></div><LogOut size={16} />
          </button>
        </div>
      </aside>

      <section className="workspace">
        <header className="topBar">
          <button className="mobileLogo" aria-label="Abrir navegação" onClick={() => setMoreOpen(true)}><Brand compact /></button>
          <div className="breadcrumb"><strong>{screenLabels?.[screen]||NAVIGATION.find((item) => item.id === screen)?.label}</strong><small>· {org.name}</small></div>
          <div title="Estado da rede e fila local; não é indicador de atualização do aplicativo." className={`connection ${online ? '' : 'offline'}`}>{online ? <Wifi size={14} /> : <CloudOff size={14} />}{online ? 'Rede ativa' : 'Modo continuidade'}{offlineCount > 0 && <b>{offlineCount}</b>}</div>
        </header>
        <main>{children}</main>
      </section>

      <nav className="bottomNav">
        {primaryMobile.map((item) => {
          const Icon = item.icon;
          return <button key={item.id} className={screen === item.id ? 'active' : ''} onClick={() => go(item.id)}><Icon size={20} /><span>{item.short}</span></button>;
        })}
        <button className={moreMobile.some((item) => item.id === screen) ? 'active' : ''} onClick={() => setMoreOpen(true)}><Menu size={20} /><span>Mais</span></button>
      </nav>

      {moreOpen && (
        <div className="mobileMenuBackdrop" onMouseDown={(event) => event.target === event.currentTarget && setMoreOpen(false)}>
          <section className="mobileMenu">
            <header><div><strong>Mais opções</strong><small>{org.name}</small></div><button className="iconButton" onClick={() => setMoreOpen(false)}><X size={20} /></button></header>
            {moreMobile.map((item) => {
              const Icon = item.icon;
              return <button key={item.id} className={screen === item.id ? 'active' : ''} onClick={() => go(item.id)}><Icon size={20} /><span>{item.label}</span><ChevronRight size={17} /></button>;
            })}
            <button className="logoutMobile" onClick={() => (isDemo ? location.assign('?demo=1') : supabase.auth.signOut())}><LogOut size={20} /><span>Sair com segurança</span></button>
          </section>
        </div>
      )}
    </div>
  );
}

function fallbackCapabilities(member) {
  const admin = ['owner', 'admin'].includes(member?.role) || member?.permissions?.pdv_admin;
  return {
    role: member?.role || 'member',
    sell: true,
    register: true,
    catalog: Boolean(admin || member?.permissions?.pdv_catalog),
    inventory: Boolean(admin || member?.permissions?.pdv_inventory),
    cash_management: Boolean(admin || member?.permissions?.pdv_cash_management),
    void_sales: Boolean(admin || member?.permissions?.pdv_void_sales),
    discount_override: Boolean(admin || member?.permissions?.pdv_discount_override),
    settings: Boolean(admin),
    audit: Boolean(admin || member?.permissions?.pdv_audit),
  };
}

function Workspace({ session }) {
  const [access, setAccess] = useState(isDemo ? { organizations: [DEMO_ORG], memberships: [{ organization_id: 'demo-org', display_name: 'Rogger P.', role: 'owner', permissions: {} }], entitlements: [{ organization_id: 'demo-org', status: 'active', settings: { edition: 'full', payment_capture_enabled: false, fiscal_issuance_enabled: false } }] } : null);
  const [organizationId, setOrganizationId] = useState(isDemo ? 'demo-org' : '');
  const [receivingCnpj,setReceivingCnpj]=useState('');
  useEffect(()=>{
    if(isDemo||!organizationId){setReceivingCnpj('');return undefined;}
    let alive=true;setReceivingCnpj('');
    supabase.from('pdv_nfe_recipient_settings').select('recipient_cnpj')
      .eq('organization_id',organizationId).maybeSingle()
      .then(({data})=>{if(alive)setReceivingCnpj(data?.recipient_cnpj||'')})
      .catch(()=>{if(alive)setReceivingCnpj('')});
    return()=>{alive=false};
  },[organizationId]);
  const [data, setData] = useState(isDemo ? cloneDemo() : null);
  const [screen, setScreen] = useState('pos');
  const [cart, setCart] = useState([]);
  const [pendingScanProduct,setPendingScanProduct]=useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [loading, setLoading] = useState(!isDemo);
  const [bootstrapError,setBootstrapError]=useState('');
  const [bootedOrganization,setBootedOrganization]=useState(isDemo?'demo-org':'');
  const [hydrationRevision,setHydrationRevision]=useState(0);
  const [catalogHydrating,setCatalogHydrating]=useState(false);
  const [catalogHydrationError,setCatalogHydrationError]=useState('');
  const bootSeq=useRef(0);
  const [notice, setNotice] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [offlineQueue, setOfflineQueue] = useState([]);
  const [heldCarts, setHeldCarts] = useState([]);
  const [sharedProducts,setSharedProducts]=useState([]);
  const [sharedOrganizationIds,setSharedOrganizationIds]=useState([]);
  const [sharedLoading,setSharedLoading]=useState(false);
  const [presaleRows,setPresaleRows]=useState([]);
  const [presaleReportState,setPresaleReportState]=useState({loading:false,complete:false,error:''});
  const [presaleSubmitting,setPresaleSubmitting]=useState(false);
  const draftPending=useRef(null);

  const organization = access?.organizations.find((item) => item.id === organizationId) || access?.organizations[0];
  const member = access?.memberships.find((item) => item.organization_id === organization?.id);
  const entitlementRecord = access?.entitlements?.find((item) => item.organization_id === organization?.id);
  const entitlement = entitlementRecord?.settings || {};
  const nonFiscalOnly = !isDemo && entitlement.commercial_checkout_enabled===false;
  const sharedGroup = data?.settings?.operations?.shared_cash_group;
  const sharedAdapter = data?.settings?.operations?.shared_presales_adapter;
  const locationRecord = data?.locations?.[0];
  const terminal = data?.terminals?.find((item) => item.location_id === locationRecord?.id) || data?.terminals?.[0];
  const register = data?.sessions?.find((item) => item.status === 'open' && item.terminal_id === terminal?.id);
  const capabilities = useMemo(() => ({...fallbackCapabilities(member),
    ...(data?.capabilities||{})}), [member,data?.capabilities]);

  const report = useCallback((message, tone = 'ok') => setNotice({ message, tone }), []);
  const failOperation = useCallback((error) => {
    report(friendlyError(error), 'bad');
    throw error;
  }, [report]);

  const reload = useCallback(async (quiet = false) => {
    if (isDemo || !organizationId) return;
    const run=++bootSeq.current;
    if (!quiet) {setLoading(true);setData(null);}
    setBootstrapError('');
    setCatalogHydrationError('');
    try {
      const nextData=await withDeadline(loadData(organizationId,{quick:true}),18000);
      if(run!==bootSeq.current)return;
      setData(nextData);
      setBootedOrganization(organizationId);
      setHydrationRevision(n=>n+1);
    } catch (error) {
      if(run!==bootSeq.current)return;
      setBootstrapError('Não foi possível carregar os dados da empresa. Verifique a conexão e tente novamente.');
      report(friendlyError(error),'bad');
    } finally {
      if (run===bootSeq.current && !quiet) setLoading(false);
    }
  }, [organizationId, report]);

  // Complete catalogue and stock load AFTER the original PDV is visible.
  // An old company request must never overwrite a newer company's records.
  useEffect(()=>{
    if(isDemo||!organizationId||bootedOrganization!==organizationId||!data)return;
    let alive=true;
    const run=bootSeq.current;
    setCatalogHydrating(true);
    setCatalogHydrationError('');
    (async()=>{
      const [products,balances]=await withDeadline(Promise.all([
        loadTenantPages('pdv_products',organizationId),
        loadAllBalances(organizationId)
      ]),45000);
      const enriched=await attachProductImages(products.sort((a,b)=>
        a.name.localeCompare(b.name,'pt-BR')));
      if(!alive||run!==bootSeq.current)return;
      setData(previous=>previous&&alive&&run===bootSeq.current?{
        ...previous,products:enriched,balances
      }:previous);
    })().catch(()=>{
      if(alive&&run===bootSeq.current)
        setCatalogHydrationError('Catálogo ainda incompleto. Toque em tentar novamente para carregar todos os produtos.');
    }).finally(()=>{if(alive&&run===bootSeq.current)setCatalogHydrating(false)});
    return ()=>{alive=false};
  },[organizationId,bootedOrganization,hydrationRevision]);

  useEffect(()=>{
    let alive=true;
    setSharedProducts([]);setSharedOrganizationIds([]);setSharedLoading(false);
    if(!nonFiscalOnly || !sharedGroup || !organization?.id || !access?.organizations?.length)return;
    const otherOrgs=access.organizations.filter(o=>o.id!==organization.id);
    setSharedLoading(true);
    (async()=>{
      const partners=[];
      for(const candidate of otherOrgs){
        const result=await supabase.from('pdv_settings').select('operations')
          .eq('organization_id',candidate.id).maybeSingle();
        if(result.error)throw result.error;
        if(result.data?.operations?.shared_cash_group===sharedGroup &&
           result.data?.operations?.shared_presales_adapter===sharedAdapter){
          const records=await loadTenantPages('pdv_products',candidate.id);
          partners.push({organization:candidate,products:records});
        }
      }
      if(!alive)return;
      if(partners.length>1)throw Error('Grupo de caixa exige configuração específica.');
      setSharedProducts(partners.flatMap(p=>p.products.map(product=>({
        ...product,organization_name:p.organization.name
      }))));
      setSharedOrganizationIds([organization.id,...partners.map(p=>p.organization.id)]);
    })().catch(()=>{if(alive)report('Falha ao consultar catálogo compartilhado. Recarregue a tela.','bad')})
      .finally(()=>{if(alive)setSharedLoading(false)});
    return()=>{alive=false};
  },[nonFiscalOnly,sharedGroup,sharedAdapter,organization?.id,access?.organizations,report]);
  const posProducts=useMemo(()=>{
    if(!data||!organization)return [];
    if(!nonFiscalOnly)return data.products;
    return [...data.products.map(p=>({...p,organization_name:organization.name,
       track_inventory:false})),
      ...sharedProducts.map(p=>({...p,track_inventory:false}))];
  },[data?.products,organization?.id,organization?.name,nonFiscalOnly,sharedProducts]);

  const reloadPresaleReports=useCallback(async()=>{
    if(!session.access_token)return;
    setPresaleReportState({loading:true,complete:false,error:''});
    try{
      const all=[];let total=0;
      for(let page=0;page<40;page++){
        const response=await fetch('/internal/smartpdv/pilot/reports?page='+page,{
          headers:{authorization:'Bearer '+session.access_token},credentials:'same-origin',cache:'no-store'});
        if(!response.ok)throw Error('Não foi possível consultar as pré-vendas. Confirme o terminal autorizado.');
        const body=await response.json();
        if(body.mode!=='presales_only'||body.payment_capture_enabled!==false||
          body.fiscal_issuance_enabled!==false||!Array.isArray(body.rows))
          throw Error('Relatório não autorizado.');
        if(page===0)total=body.total_count;
        all.push(...body.rows);
        if(!body.has_more){
          if(all.length!==total)throw Error('Relatório incompleto; atualize novamente.');
          setPresaleRows(all);
          setPresaleReportState({loading:false,complete:true,error:''});
          return;
        }
      }
      throw Error('Histórico muito extenso. Utilize a exportação paginada.');
    }catch(error){
      setPresaleRows([]);
      setPresaleReportState({loading:false,complete:false,error:error.message});
    }
  },[session.access_token]);
  useEffect(()=>{
    if(nonFiscalOnly&&(screen==='dashboard'||screen==='audit'))reloadPresaleReports();
  },[nonFiscalOnly,screen,reloadPresaleReports]);

  const saveSharedDraft=useCallback(async ({items,discountCents})=>{
    if(!nonFiscalOnly||sharedAdapter!=='casa_consert_v1'||sharedLoading||sharedOrganizationIds.length!==2)
      throw Error('Grupo de pré-vendas indisponível. Confirme as duas empresas e atualize.');
    if(!navigator.onLine)throw Error('É necessário estar conectado para salvar uma pré-venda.');
    if(discountCents!==0)throw Error('Descontos de venda ainda não homologados.');
    const catalog=new Map(posProducts.map(p=>[p.id,p]));
    const entries=items.map(i=>{
      const product=catalog.get(i.product_id);
      if(!product||product.active!==true||product.price_cents!==i.unit_price_cents||i.discount_cents)
        throw Error('Produto ou preço alterado. Atualize o catálogo antes de salvar.');
      return {id:i.product_id,organization_id:product.organization_id,
        quantity:i.quantity,price_cents:product.price_cents,active:product.active};
    });
    const snapshot=prepareSharedPresale(entries,sharedOrganizationIds);
    const signature=JSON.stringify(snapshot);
    if(draftPending.current?.signature!==signature)
      draftPending.current={signature,key:'pre_'+uuid().replaceAll('-','')};
    const result=await fetch('/internal/smartpdv/pilot/presales',{
      method:'POST',headers:{'content-type':'application/json',
        authorization:'Bearer '+session.access_token},
      credentials:'same-origin',cache:'no-store',
      body:JSON.stringify({action:'create',presale_key:draftPending.current.key,snapshot})
    });
    if(!result.ok){
      const failure=await result.json().catch(()=>({}));
      if(result.status===401)throw Error('Sua sessão expirou. Entre novamente no SmartPDV.');
      if(result.status===403)throw Error('Pré-venda não autorizada. Abra Configurações, ative este navegador e confira o vínculo com as duas empresas.');
      if(result.status===404||result.status===503)throw Error('O servidor ainda não liberou a rota de pré-vendas neste domínio. Solicite revisão da publicação.');
      if(result.status===409)throw Error('O preço ou cadastro mudou. Atualize o catálogo e confira o carrinho.');
      if(failure?.error==='two_tenant_membership_or_entitlement_required')
        throw Error('A identidade foi validada, mas o acesso às duas empresas ou a licença de pré-venda precisa ser conferido pelo gestor.');
      if(failure?.error==='identity_provider_unavailable'){
        const phase={
          casa_auth:'autorização Casa da Limpeza',
          casa_auth_user_network:'comunicação com Supabase Auth',
          casa_auth_user_payload:'resposta do Supabase Auth',
          casa_auth_members:'vínculo da Casa da Limpeza',
          casa_auth_organizations:'cadastro da Casa da Limpeza',
          casa_auth_product:'catálogo de licenças SmartPDV',
          casa_auth_license:'licença da Casa da Limpeza',
          consert_auth:'validação da conta ConsertCell',
          consert_membership:'vínculo do operador ConsertCell',
          product_entitlement:'catálogo de licenças SmartPDV',
          consert_organization:'cadastro da ConsertCell',
          consert_license:'licença de pré-vendas da ConsertCell',
          identity_unknown:'identidade do operador'
        }[failure.verification_stage]||'validação de identidade';
        throw Error('O servidor não conseguiu validar sua identidade ('+phase+'). Para sua segurança, não repita o salvamento antes de verificar o histórico da pré-venda.');
      }
      if(failure?.error==='queue_storage_unavailable')
        throw Error('A gravação pode ter ocorrido sem confirmação. Confira o histórico antes de tentar novamente. Carrinho preservado.');
      throw Error('Não foi possível salvar. Nenhuma pré-venda ou pagamento foi confirmado.');
    }
    if(!(result.headers.get('content-type')||'').includes('application/json'))
      throw Error('A rota de pré-venda retornou uma página em vez da resposta esperada. A publicação do servidor precisa ser corrigida.');
    const body=await result.json().catch(()=>{throw Error('O servidor respondeu com dados inválidos. Confirme a publicação das Functions.');});
    if(body.mode!=='presales_only'||body.commercial_operations_enabled!==false
       ||body.fiscal_issuance_enabled!==false)throw Error('Resposta de pré-venda inválida.');
    draftPending.current=null;setCart([]);
    report('Pré-venda não fiscal registrada, sem cobrança ou baixa de estoque.');
    return {presale_only:true};
  },[nonFiscalOnly,sharedAdapter,sharedLoading,sharedOrganizationIds,posProducts,session.access_token,report]);

  const syncOffline = useCallback(async (announce = true) => {
    if (nonFiscalOnly || isDemo || !organizationId || !navigator.onLine) return;
    const queue = listQueuedCheckouts(organizationId);
    if (!queue.length) return;
    const result = await flushQueuedCheckouts(organizationId, async (payload) => value(await supabase.rpc('pdv_checkout_sale', { p_payload: payload })));
    setOfflineQueue(result.remaining);
    if (result.delivered.length) {
      await reload(true);
      if (announce) report(`${result.delivered.length} venda(s) offline sincronizada(s).`);
    }
    if (result.remaining.length && announce) report('A fila ainda possui operações pendentes.', 'bad');
  }, [organizationId, reload, report, nonFiscalOnly]);

  useEffect(() => {
    if (isDemo) return;
    let alive=true;
    withDeadline(loadAccess(session.user.id),16000)
      .then((nextAccess) => {
        if(!alive)return;
        const target = resolveSmartPdvTenant(nextAccess.organizations, requestedOrganizationSlug, nextAccess.preferredOrganizationSlug);
        setAccess(nextAccess);
        setOrganizationId(target.organizationId);
        if (target.error) setBootstrapError(target.error);
        setLoading(false);
      })
      .catch((error) => {
        if(!alive)return;
        setBootstrapError('Não foi possível verificar seu acesso. Confira a conexão e tente novamente.');
        report(friendlyError(error), 'bad');
        setLoading(false);
      });
    return ()=>{alive=false};
  }, [session.user.id, report]);

  useEffect(() => {
    if (organizationId) {
      setOfflineQueue(listQueuedCheckouts(organizationId));
      setHeldCarts(listHeldCarts(organizationId));
      try {
        setCart(JSON.parse(localStorage.getItem(`kaizen.smartpdv.cart.${organizationId}`) || '[]'));
      } catch {
        setCart([]);
      }
      reload();
    }
  }, [organizationId, reload]);

  useEffect(() => {
    if (organizationId) localStorage.setItem(`kaizen.smartpdv.cart.${organizationId}`, JSON.stringify(cart));
  }, [cart, organizationId]);

  useEffect(() => {
    const update = () => {
      setOnline(navigator.onLine);
      if (navigator.onLine) syncOffline(false).catch(() => {});
    };
    addEventListener('online', update);
    addEventListener('offline', update);
    return () => {
      removeEventListener('online', update);
      removeEventListener('offline', update);
    };
  }, [syncOffline]);

  useEffect(() => {
    if (!data?.settings) return;
    applySmartPdvTheme(data.settings.theme || {});
  }, [data?.settings]);

  async function run(real, demo, successMessage) {
    try {
      if (isDemo) demo?.();
      else {
        await real();
        await reload(true);
      }
      if (successMessage) report(successMessage);
    } catch (error) {
      failOperation(error);
    }
  }

  async function checkout({ items, payments, customerId, discountCents, notes }) {
    // A client-side convenience guard; the immutable server-side sales
    // trigger also rejects checkout for this tenant unless licensed.
    if(entitlement.commercial_checkout_enabled === false){
      return failOperation(new Error('Vendas e recebimentos ainda não estão liberados. Utilize pré-vendas.'));
    }
    const key = uuid();
    const payload = checkoutPayload({
      organizationId: organization.id,
      locationId: locationRecord.id,
      terminalId: terminal.id,
      sessionId: register.id,
      items,
      payments,
      customerId,
      discountCents,
      notes,
      idempotencyKey: key,
      source: navigator.onLine ? 'smart-pdv' : 'smart-pdv-offline',
    });
    const cartSummary = totals(items, discountCents);
    try {
      let result;
      if (isDemo) {
        result = {
          id: key,
          sale_number: Math.max(1018, ...data.sales.map((sale) => sale.sale_number)) + 1,
          status: 'completed',
          subtotal_cents: cartSummary.subtotalCents,
          discount_cents: cartSummary.discountCents,
          total_cents: cartSummary.totalCents,
          change_cents: Math.max(0, payments.reduce((sum, payment) => sum + payment.amount_cents, 0) - cartSummary.totalCents),
          receipt_type: 'non_fiscal',
          created_at: now(),
        };
        setData((current) => ({
          ...current,
          sales: [{ ...result, payment_summary: payments, customer_id: customerId || null }, ...current.sales],
          balances: current.balances.map((balance) => {
            const item = items.find((line) => line.product_id === balance.product_id);
            return item ? { ...balance, quantity: Number(balance.quantity) - Number(item.quantity) } : balance;
          }),
        }));
      } else {
        result = value(await supabase.rpc('pdv_checkout_sale', { p_payload: payload }));
        await reload(true);
      }
      setCart([]);
      setReceipt({ ...result, items, payments, order_discount_cents: discountCents });
      report(`Venda #${result.sale_number} concluída.`);
      return result;
    } catch (error) {
      const cashOnly = payments.every((payment) => payment.method === 'cash');
      if (!isDemo && isRetryableCheckoutError(error, navigator.onLine) && cashOnly) {
        const queued = enqueueCheckout(organization.id, {
          payload: { ...payload, integration_metadata: { ...payload.integration_metadata, offline_replay: true } },
          receipt: { items, payments, order_discount_cents: discountCents, total_cents: cartSummary.totalCents },
        });
        setOfflineQueue(queued);
        setCart([]);
        const provisional = `OFF-${key.slice(0, 8).toUpperCase()}`;
        setReceipt({ offline: true, provisional_number: provisional, items, payments, order_discount_cents: discountCents, total_cents: cartSummary.totalCents, created_at: now() });
        report(`Venda ${provisional} preservada para sincronização.`, 'warning');
        return { offline: true, provisional_number: provisional };
      }
      if (!cashOnly && isRetryableCheckoutError(error, navigator.onLine)) {
        return failOperation(new Error('Pix e cartão exigem conexão. Use dinheiro ou aguarde a sincronização.'));
      }
      return failOperation(error);
    }
  }

  async function identifyProduct(images) {
    if (isDemo) throw new Error('O reconhecimento por IA exige login real em um tenant autorizado.');
    if (!images?.length || images.length>3) throw new Error('Escolha de 1 a 3 imagens.');
    const temporary=[];
    try {
      const storagePaths=[];
      for (const image of images) {
        if (image.path) {
          if (!image.path.startsWith(organization.id + '/products/') || image.path.includes('..')) {
            throw new Error('Imagem fora do tenant ativo.');
          }
          storagePaths.push(image.path);
        } else if (image.file) {
          const uploaded=await uploadProductPhoto(organization.id,image.file);
          temporary.push(uploaded.image_path);
          storagePaths.push(uploaded.image_path);
        }
      }
      const {data:result,error}=await supabase.functions.invoke('smartpdv-product-enrich',{
        body:{organization_id:organization.id,storage_paths:storagePaths}
      });
      if (error || !result?.ok) {
        throw new Error(result?.error||error?.message||'Serviço de IA temporariamente indisponível.');
      }
      return result;
    } finally {
      if (temporary.length) {
        await supabase.storage.from(PRODUCT_IMAGE_BUCKET).remove(temporary).catch(()=>{});
      }
    }
  }

  async function ensureCategory(name){
    if(isDemo){
      const existing=data.categories.find(item=>item.name.trim().toLocaleLowerCase('pt-BR')===name.trim().toLocaleLowerCase('pt-BR'));
      if(existing)return {id:existing.id,name:existing.name,created:false};
      const category={id:uuid(),organization_id:organization.id,name:name.trim(),color:'#6d63ff',sort_order:0,active:true};
      setData(current=>({...current,categories:[...current.categories,category]}));
      return {id:category.id,name:category.name,created:true};
    }
    const result=value(await supabase.rpc('pdv_ensure_category',{
      p_organization_id:organization.id,p_name:name.trim()
    }));
    if(!result?.id)throw new Error('Não foi possível gravar a categoria.');
    setData(current=>({
      ...current,
      categories:current.categories.some(item=>item.id===result.id)
        ?current.categories:[...current.categories,{id:result.id,organization_id:organization.id,
          name:result.name,color:'#6d63ff',active:true,sort_order:0}].sort((a,b)=>a.name.localeCompare(b.name,'pt-BR'))
    }));
    return result;
  }

  async function ensureSubcategory(parentId,name){
    if(!parentId||!name?.trim())throw new Error('Escolha a categoria antes da subcategoria.');
    if(isDemo){
      const found=data.categories.find(c=>c.parent_id===parentId&&
        c.name.toLocaleLowerCase('pt-BR')===name.trim().toLocaleLowerCase('pt-BR'));
      if(found)return {id:found.id,name:found.name,parent_id:parentId,created:false};
      const category={id:uuid(),parent_id:parentId,organization_id:organization.id,
        name:name.trim(),color:'#6d63ff',active:true,sort_order:0};
      setData(current=>({...current,categories:[...current.categories,category]}));
      return {...category,created:true};
    }
    const result=value(await supabase.rpc('pdv_ensure_subcategory',{
      p_organization_id:organization.id,p_parent_id:parentId,p_name:name.trim()
    }));
    if(!result?.id)throw new Error('Não foi possível gravar a subcategoria.');
    setData(current=>({
      ...current,categories:current.categories.some(item=>item.id===result.id)
        ?current.categories:[...current.categories,{id:result.id,organization_id:organization.id,
          parent_id:parentId,name:result.name,color:'#6d63ff',sort_order:0,active:true}]
    }));
    return result;
  }

  async function saveProduct(product, photo, additionalPhotos=[], openingQty=0) {
    if(openingQty>0 && product.id)throw new Error('Entradas em produtos existentes devem ser realizadas em Estoque.');
    if(!Number.isFinite(openingQty)||openingQty<0||openingQty>1000000||
      (openingQty%1!==0&&!product.allow_fractional))
      throw new Error('Quantidade inicial inválida.');
    const uploadedPaths = [];
    try {
      let cover={};
      if (photo) {
        cover = isDemo
          ? {image_path:'demo/products/'+uuid(),image_mime_type:photo.type,image_url:URL.createObjectURL(photo)}
          : await uploadProductPhoto(organization.id,photo);
        if (!isDemo) uploadedPaths.push(cover.image_path);
      }
      let gallery=[];
      for (const item of additionalPhotos.slice(0,2)) {
        if (item.file) {
          const media = isDemo
            ? {image_path:'demo/products/'+uuid(),image_mime_type:item.file.type,image_url:item.preview}
            : await uploadProductPhoto(organization.id,item.file);
          if (!isDemo) uploadedPaths.push(media.image_path);
          gallery.push({path:media.image_path,mime_type:media.image_mime_type,image_url:media.image_url});
        } else if (item.path && (isDemo || item.path.startsWith(organization.id+'/products/'))) {
          gallery.push({path:item.path,mime_type:item.mime_type||'image/webp',image_url:item.preview||null});
        }
      }
      const {opening_stock: _transientOpening, ...productFields}=product;
      const payload = {...productFields,...cover};
      // A cover remains the same field consumed by Repair and the cashier.
      if (!payload.image_path && gallery.length) {
        const first = gallery.shift();
        payload.image_path=first.path;
        payload.image_mime_type=first.mime_type;
        payload.image_url=first.image_url;
      }
      gallery = normalizeGalleryImages(gallery,payload.image_path,isDemo?undefined:organization.id)
        .map(item=>({...item,image_url:gallery.find(g=>g.path===item.path)?.image_url||null}));
      payload.metadata={
        ...(product.metadata||{}),
        gallery_images:gallery.map(({path,mime_type})=>({path,mime_type})),
      };
      if (isDemo) {
        const createdId=payload.id||uuid();
        setData((current)=>({
          ...current,
          products:payload.id
            ? current.products.map(item=>item.id===payload.id?{...item,...payload,metadata:{...payload.metadata,gallery_images:gallery}}:item)
            : [...current.products,{...payload,id:createdId,active:true,metadata:{...payload.metadata,gallery_images:gallery}}],
          balances:openingQty>0?[...current.balances,{
            organization_id:organization.id,location_id:locationRecord.id,
            product_id:createdId,quantity:openingQty
          }]:current.balances,
          inventoryMovements:openingQty>0?[{
            id:uuid(),product_id:createdId,location_id:locationRecord.id,
            movement_type:'adjustment',quantity_delta:openingQty,
            balance_after:openingQty,reason:'Estoque inicial no cadastro',created_at:now()
          },...current.inventoryMovements]:current.inventoryMovements,
        }));
      } else {
        if(openingQty>0){
          value(await supabase.rpc('pdv_create_product_with_opening_stock',{
            p_organization_id:organization.id,
            p_location_id:locationRecord.id,
            p_payload:payload,p_opening_quantity:openingQty
          }));
        }else{
          value(await supabase.rpc('pdv_upsert_product',{
            p_organization_id:organization.id,p_payload:payload
          }));
        }
        await reload(true);
      }
      report(openingQty>0
        ? 'Produto salvo e quantidade inicial lançada no estoque.'
        : 'Produto, galeria e informações comerciais salvos.');
      return payload;
    } catch(error) {
      if(uploadedPaths.length && !isDemo){
        await supabase.storage.from(PRODUCT_IMAGE_BUCKET).remove(uploadedPaths).catch(()=>{});
      }
      return failOperation(error);
    }
  }

  if (loading || (!bootstrapError && !isDemo && organizationId && bootedOrganization !== organizationId)) return <div className="loading"><Brand /><Loader2 className="spin" /><span>Preparando empresa...</span></div>;
  if (bootstrapError) return <div className="blocked"><AlertTriangle size={34}/>
    <h1>Não foi possível concluir o carregamento</h1><p>{bootstrapError}</p>
    <Button onClick={()=>{if(organizationId)reload();else window.location.reload()}}>Tentar novamente</Button>
    <Button kind="ghost" onClick={()=>supabase.auth.signOut()}>Trocar usuário</Button></div>;
  if (!access?.organizations.length) return <div className="blocked"><ShieldCheck size={34} /><h1>SmartPDV ainda não liberado</h1><p>Sua identidade está válida, mas nenhuma empresa possui entitlement ativo para este produto.</p><Button kind="ghost" onClick={() => supabase.auth.signOut()}>Sair</Button></div>;
  if (!data || !organization || !locationRecord || !terminal) return <div className="blocked">
    <AlertTriangle size={34}/><h1>Dados da empresa indisponíveis</h1>
    <p>Não foi possível iniciar o terminal. Seus produtos continuam preservados.</p>
    <Button onClick={()=>reload()}>Recarregar empresa</Button></div>;

  const common = { data, session: register, capabilities };
  let view;
  if (screen === 'pos') {
    view = <Pos {...common} data={nonFiscalOnly?{...data,products:posProducts}:data}
      session={nonFiscalOnly?null:register} presaleOnly={nonFiscalOnly}
      operatorMode={true} cart={cart} setCart={setCart} location={locationRecord}
      terminal={terminal} org={organization} heldCarts={nonFiscalOnly?[]:heldCarts}
      onHold={({cart:heldCart,...metadata})=>{
        if(nonFiscalOnly)return;
        setHeldCarts(holdCart(organization.id,heldCart,metadata));report('Venda suspensa neste dispositivo.');
      }}
      onResume={held=>{if(nonFiscalOnly)return;setCart(held.cart);
        setHeldCarts(removeHeldCart(organization.id,held.id));}}
      onCheckout={nonFiscalOnly?saveSharedDraft:checkout} setScreen={setScreen}
      onCreateFromScan={barcode=>{if(!capabilities.catalog)return;
        setPendingScanProduct({barcode,token:crypto.randomUUID()});setScreen('products')}} />;
  } else if (screen === 'dashboard' && nonFiscalOnly) {
    view = <PresalesReport presales={presaleRows} loading={presaleReportState.loading}
      queueError={presaleReportState.error} completeSource={presaleReportState.complete}
      onRefresh={reloadPresaleReports}
      onSubmit={()=>{throw Error('Encaminhamento disponível após ativação de terminal.')}} submitting={presaleSubmitting}/>;
  } else if (screen === 'dashboard') {
    view = <Dashboard {...common} location={locationRecord} offlineCount={offlineQueue.length} setScreen={setScreen} onVoid={(saleId, reason) => run(() => value(supabase.rpc('pdv_void_sale', { p_organization_id: organization.id, p_sale_id: saleId, p_reason: reason })), () => setData((current) => ({ ...current, sales: current.sales.map((sale) => sale.id === saleId ? { ...sale, status: 'cancelled' } : sale) })), 'Venda cancelada e saldos revertidos.')} />;
  } else if (screen === 'products') {
    view = <Products data={data} capabilities={capabilities} onSave={saveProduct} onIdentify={identifyProduct} onEnsureCategory={ensureCategory} onEnsureSubcategory={ensureSubcategory} entitlement={entitlement} location={locationRecord} initialBarcode={pendingScanProduct} onConsumeInitialBarcode={()=>setPendingScanProduct(null)} />;
  } else if (screen === 'import') {
    view = <CatalogImport data={data} organization={organization} location={locationRecord}
      capabilities={capabilities} isDemo={isDemo}
      onCommit={async (batch)=>{
        const response=await supabase.rpc('pdv_import_products_batch_v1',{
          p_organization_id:organization.id,p_location_id:locationRecord.id,p_items:batch
        });
        return value(response);
      }}
      onDone={async()=>{await reload(true);report('Importação concluída. Confira o catálogo e o estoque.');}}/>;
  } else if (screen === 'stock') {
    view = <><NfeReceiving key={organization.id} organization={organization} products={data.products}
      catalogReady={!catalogHydrating&&!catalogHydrationError&&bootedOrganization===organization.id}
      recipientCnpj={receivingCnpj} canInventory={capabilities.inventory}
      onConfigureCnpj={['owner','admin'].includes(member?.role)?async(cnpj)=>{
        value(await supabase.rpc('pdv_nfe_configure_recipient',{
          p_organization_id:organization.id,p_recipient_cnpj:cnpj}));
        setReceivingCnpj(cnpj);
      }:undefined}
      onAnalyzeDanfe={async(documents)=>{
        if(isDemo||!supabase||!capabilities.inventory||!organization?.id)
          throw Error('A análise de DANFE exige conta autorizada e permissão de estoque.');
        const {data:result,error}=await supabase.functions.invoke('smartpdv-product-enrich',{
          body:{organization_id:organization.id,mode:'danfe_review',documents}
        });
        if(error||result?.ok!==true)
          throw Error(result?.error||error?.message||'Análise visual indisponível no momento.');
        return result;
      }}
      onPost={async(payload)=>{
        const receipt=value(await supabase.rpc('pdv_nfe_post_receipt',{
          p_organization_id:organization.id,p_location_id:locationRecord.id,p_payload:payload}));
        return receipt;
      }} onReload={()=>reload(true)}/>
      <Stock data={data} location={locationRecord} capabilities={capabilities} onAdjust={({ productId, quantityDelta, reason }) => run(() => value(supabase.rpc('pdv_adjust_stock', { p_organization_id: organization.id, p_location_id: locationRecord.id, p_product_id: productId, p_quantity_delta: quantityDelta, p_reason: reason })), () => setData((current) => ({ ...current, balances: current.balances.map((balance) => balance.product_id === productId ? { ...balance, quantity: Number(balance.quantity) + quantityDelta } : balance), inventoryMovements: [{ id: uuid(), product_id: productId, movement_type: 'adjustment', quantity_delta: quantityDelta, balance_after: Number(current.balances.find((balance) => balance.product_id === productId)?.quantity || 0) + quantityDelta, reason, created_at: now() }, ...current.inventoryMovements] })), 'Estoque ajustado.')} /></>;
  } else if (screen === 'customers') {
    view = <Customers data={data} onSave={(customer) => run(() => value(supabase.rpc('pdv_upsert_customer', { p_organization_id: organization.id, p_payload: customer })), () => setData((current) => ({ ...current, customers: customer.id ? current.customers.map((item) => item.id === customer.id ? { ...item, ...customer } : item) : [...current.customers, { ...customer, id: uuid(), status: 'active' }] })), 'Cliente salvo.')} />;
  } else if (screen === 'cash') {
    view = <Cash {...common} onOpen={({ openingCents }) => run(() => value(supabase.rpc('pdv_open_register', { p_organization_id: organization.id, p_location_id: locationRecord.id, p_terminal_id: terminal.id, p_opening_cents: openingCents })), () => setData((current) => ({ ...current, sessions: [{ id: 'demo-session', terminal_id: terminal.id, location_id: locationRecord.id, status: 'open', opening_cents: openingCents, opened_at: now() }, ...current.sessions], movements: [{ id: uuid(), session_id: 'demo-session', movement_type: 'opening', amount_cents: openingCents, reason: 'Abertura do caixa', created_at: now() }, ...current.movements] })), 'Caixa aberto.')} onClose={({ declaredCents, notes }) => run(() => value(supabase.rpc('pdv_close_register', { p_organization_id: organization.id, p_session_id: register.id, p_declared_cents: declaredCents, p_notes: notes })), () => setData((current) => ({ ...current, sessions: current.sessions.map((item) => item.id === register.id ? { ...item, status: 'closed', closed_at: now(), closing_declared_cents: declaredCents } : item) })), 'Caixa fechado com conciliação registrada.')} onMove={({ type, amountCents, reason }) => run(() => value(supabase.rpc('pdv_record_cash_movement_v2', { p_organization_id: organization.id, p_session_id: register.id, p_movement_type: type, p_amount_cents: amountCents, p_reason: reason, p_idempotency_key: uuid() })), () => setData((current) => ({ ...current, movements: [{ id: uuid(), session_id: register.id, movement_type: type, amount_cents: amountCents, reason, created_at: now() }, ...current.movements] })), 'Movimento de caixa registrado.')} />;
  } else if (screen === 'audit' && nonFiscalOnly) {
    view = <PresalesReport presales={presaleRows} loading={presaleReportState.loading}
      queueError={presaleReportState.error} completeSource={presaleReportState.complete}
      onRefresh={reloadPresaleReports}
      onSubmit={()=>{throw Error('Encaminhamento disponível após ativação de terminal.')}} submitting={presaleSubmitting}/>;
  } else if (screen === 'audit') {
    view = <Audit data={data} location={locationRecord} />;
  } else if (screen === 'staff' && capabilities.settings) {
    view = <StaffManagement key={organization.id} organization={organization} client={supabase}/>;
  } else {
    view = <>
      {screen==='settings'&&<div className="smartpdvBuildInfo" role="note">
        <strong>Versão do SmartPDV: {RELEASE_SHA || 'não identificada'}</strong>
        <small>O indicador de rede mostra conectividade, não a versão publicada.</small>
      </div>}
      {nonFiscalOnly&&screen==='settings'&&<div className="admin">
        <DeviceActivationPilot accessToken={session.access_token}/>
        {['owner','admin'].includes(member?.role)&&
          <TerminalOperatorAdminPilot key={organization.id} accessToken={session.access_token}
            activeOrganizationId={organization.id} availableOrganizations={access.organizations}/>}
      </div>}
      <Settings data={data} entitlement={entitlement} capabilities={capabilities} offlineCount={offlineQueue.length} onSync={() => syncOffline(true)} onThemePreview={applySmartPdvTheme} onSave={(settings) => run(() => value(supabase.rpc('pdv_save_settings', { p_organization_id: organization.id, p_patch: settings })), () => setData((current) => ({ ...current, settings: { ...current.settings, display_name: settings.display_name, theme: settings.theme, operations: settings.operations, receipt: settings.receipt } })), 'Configurações salvas.')} />
    </>;
  }

  function switchOrganization(organizationId) {
    const permitted = access?.organizations.find(item => item.id === organizationId);
    if (!permitted) return;
    setOrganizationId(organizationId);
    const url = new URL(location.href);
    if (permitted.slug) url.searchParams.set('org', permitted.slug);
    else url.searchParams.delete('org');
    history.replaceState({}, '', url.pathname + url.search + url.hash);
  }

  return (
    <>
      <Layout screen={screen} setScreen={setScreen} org={organization} orgs={access.organizations}
        setOrg={switchOrganization} user={member?.display_name || session.user.email}
        online={online} register={nonFiscalOnly?null:register} offlineCount={offlineQueue.length}
        capabilities={capabilities}
        enabledScreens={nonFiscalOnly?['pos','dashboard','products','import','stock','customers','audit','settings','staff']:null}
        screenLabels={nonFiscalOnly?{pos:'Frente de pré-vendas',dashboard:'Relatórios de pré-vendas',
          audit:'Histórico de pré-vendas'}:null}>
        {(catalogHydrating||catalogHydrationError)&&<section className="catalogSyncStatus" role="status">
          <span>{catalogHydrationError||'Carregando catálogo e estoque completos. Os itens já exibidos podem ser consultados.'}</span>
          {catalogHydrationError&&<button type="button"
            onClick={()=>setHydrationRevision(n=>n+1)}>Tentar novamente</button>}
        </section>}
        {view}
      </Layout>
      <ReceiptModal receipt={receipt} settings={data.settings} onClose={() => setReceipt(null)} />
      {notice && <Notice {...notice} onClose={() => setNotice(null)} />}
    </>
  );
}

function OperatorInviteClaim({session}){
 const [state,setState]=useState('checking');
 useEffect(()=>{
  const token=new URLSearchParams(location.search).get('invite');
  if(!token){setState('done');return}
  let alive=true;
  supabase.rpc('pdv_accept_operator_invite',{p_token:token})
   .then(({data,error})=>{
    if(!alive)return;
    if(error||!data?.ok){setState('error');return}
    const url=new URL(location.href);url.searchParams.delete('invite');
    history.replaceState({},'',url.pathname+(url.search||'')+(url.hash||''));
    setState('done');
   }).catch(()=>{if(alive)setState('error')});
  return()=>{alive=false}
 },[session.user.id]);
 if(state==='checking')return <div className="loading"><Loader2 className="spin"/>Validando convite de operador…</div>;
 if(state==='error')return <div className="blocked"><ShieldCheck size={34}/>
  <h1>Convite não validado</h1><p>Confira se ele não expirou e se você entrou com o mesmo e-mail convidado.</p>
  <Button onClick={()=>supabase.auth.signOut()}>Trocar conta</Button></div>;
 return <Workspace session={session}/>;
}

function App() {
  const [session, setSession] = useState(isDemo ? { user: { id: 'demo-user', email: 'operador@kaizen.demo' } } : null);
  const [loading, setLoading] = useState(!isDemo && Boolean(supabase));
  const [recovering, setRecovering] = useState(initialRecovery);
  useEffect(() => {
    if (isDemo || !supabase) return undefined;
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session || null);
      setLoading(false);
    });
    const { data } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (event === 'PASSWORD_RECOVERY') setRecovering(true);
      setSession(nextSession);
    });
    return () => data.subscription.unsubscribe();
  }, []);
  useEffect(() => {
    if ('serviceWorker' in navigator && import.meta.env.PROD) navigator.serviceWorker.register('/sw.js').catch(() => {});
  }, []);
  function leaveRecovery() {
    history.replaceState({}, '', location.pathname);
    setRecovering(false);
  }
  if (!isDemo && !supabase) return <div className="blocked"><AlertTriangle size={34} /><h1>Configuração pendente</h1><p>Defina VITE_SUPABASE_URL e VITE_SUPABASE_PUBLISHABLE_KEY.</p></div>;
  if (loading) return <div className="loading"><Loader2 className="spin" /></div>;
  if (!isDemo && recovering) return session ? <PasswordRecovery onComplete={leaveRecovery} /> : <div className="blocked"><LockKeyhole size={34} /><h1>Link inválido ou expirado</h1><p>Solicite um novo link de recuperação na tela de acesso.</p><Button onClick={leaveRecovery}>Voltar ao acesso</Button></div>;
  // Every licensed tenant uses the ONE canonical Workspace. The legacy
  // preview-only router is never used as a customer-facing replacement.
  return session ? (new URLSearchParams(location.search).has('invite') ? <OperatorInviteClaim session={session}/> : <Workspace session={session}/>) : <Login />;
}

class AppRecoveryBoundary extends React.Component {
  constructor(props){
    super(props);
    this.state={failed:false};
  }
  static getDerivedStateFromError(){return {failed:true};}
  componentDidCatch(error,info){
    // Do not log user input, images or tenant records.
    console.error('[smartpdv] render_recovery_required',error?.name||'Error',info?.componentStack?.slice(0,400));
  }
  render(){
    if(this.state.failed){
      return <main className="smartpdvRecovery" role="alert">
        <div>
          <strong>Não foi possível mostrar esta tela</strong>
          <p>O SmartPDV encontrou uma falha de exibição. Seus produtos salvos não foram alterados.</p>
          <button type="button" onClick={()=>window.location.reload()}>Recarregar o SmartPDV</button>
          <small>Se persistir, abra o SmartPDV pelo Chrome e informe em qual tela ocorreu o erro.</small>
        </div>
      </main>;
    }
    return this.props.children;
  }
}

createRoot(document.getElementById('root')).render(
  <React.StrictMode><AppRecoveryBoundary><App /></AppRecoveryBoundary></React.StrictMode>
);
